/**
 * Shared read of a joined calendar projection before consumer formatting.
 * Raw timed events win ID collisions; persistent facades add occurrences.
 * Cache slots belong to one stable bind snapshot and must be replaced when
 * cells or constituents change. This read owns no subscription or merged cache.
 */
import { DateTime, NaiveDate, type TimezoneRegion, type Utc } from "@tento-chrono";
import { invariant } from "@tento-core/assert";
import type { EventId, EventItemAllDay, EventItemTimed, ModelRowReads, Option, RowLocalId, RowNetworkKey } from "@tento-lona/sheets";
import { EVENTS_FACADE, SourceRow, type EventsCacheSlots, type EventsFacade } from "@tento-lona/sheets/platform";
import { liveConstituentRowsOf, liveRowOf } from "@tento-lona/sheets/internal-ui";
import type { AuthScopedCalendarId } from "../events/calendar-types.ts";
import {
  type CalendarListConfig, calendarListConfig, calendarListVerdict, decodeCalendarListAttributes,
} from "./calendar-list-membership.ts";
import type { SheetRow } from "../rows/sheet-row.ts";
import {
  type AllDayLanes, CalendarReadLanes, joinedNode, modelNode, type MonthSourceNode, type ReadNode, type TimedLanes,
} from "./calendar-read-lanes.ts";

/** SDK calendar semantics; consumers supply their resolved UTC/date bounds. */
export interface CalendarEventRead {
  /**
   * The timed events that BELONG to `range` under lona's one half-open
   * membership convention (`TimeMembership`: an event ending at midnight
   * belongs to the day it ends before; an instant to the period it starts
   * in) — the membership `EventsFacade.peekInRange` applies. Raw events win
   * ID collisions before membership is applied, so precedence does not
   * depend on the range. One item per id, by start, then id (an expanded
   * occurrence sits among the stored events by its start). A read changes
   * nothing: `generation()` stays put across reads.
   */
  timed(range: DateTime.Range<Utc>, cache: Option<EventsCacheSlots>): readonly EventItemTimed[];
  /**
   * The all-day events whose days meet `range` (a half-open day range: an
   * item covering days `start..end` inclusive meets it when
   * `start < range.end` and `range.start <= end`). The lane (raw, aggregate
   * or facade) is chosen as before; only the items outside `range` the
   * providers' inclusive lookups also answer are left out. One item per
   * (id, source row): the same event reached twice from one row is answered
   * once, the same id from two rows once per row. By start, then id (one
   * id's rows in the order reached). `cache` is the bind's cache, as
   * `timed` takes it: the aggregate lane's seeded indices are held there for
   * the bind; `null` reads none (a bind that reads its all-day row once,
   * as the calendar list's does, has nothing to hold). A consumer reading
   * many day ranges of one bind reads them from one `span` instead.
   */
  allDay(range: NaiveDate.Range, cache: Option<EventsCacheSlots>): readonly EventItemAllDay[];
  /**
   * One read of `range` (a strip's week) that the consumer slices per day or
   * column: every lane is read once, over the whole span, and each slice
   * filters it. For every `sub` inside `range`, `span.timed(sub)` answers
   * exactly what `this.timed(sub, cache)` would (members, raw-first
   * precedence, EXDATE handling, origins and order), and for every day range
   * inside the span's days `span.allDay(days)` answers what
   * `this.allDay(days, cache)` would, its raw lane read through `cache`.
   * The span's days are `range`'s in `zone`, the zone the consumer's columns
   * are days of (the viewer's): from the local date `range` starts on to the
   * local date it ends on. A frame of day columns bounded by `zone`'s local
   * midnights spans exactly those columns' days, east of UTC as west.
   * `zone` is required: a `null` zone takes the days as UTC's dates, which
   * east of UTC end a day short of such a frame's last column, so a consumer
   * slicing a viewer's days passes the viewer's zone and only a consumer
   * that has no zone (its columns are UTC days) passes `null`.
   */
  span(range: DateTime.Range<Utc>, cache: Option<EventsCacheSlots>, zone: Option<TimezoneRegion>): CalendarEventRead.Span;
  /**
   * A number that advances whenever anything `timed` / `allDay` / `span`
   * would read for this row changes: a constituent's events (a cell change
   * announced for its row), an index change (a rebuild, an expansion
   * install, a master-slot suppression), or the constituent set (a source
   * moving in or out, a facade appearing). Unchanged state reads the same
   * number, so a consumer keys a completed read's reuse ticket on it
   * instead of a sheet-wide clock. Numbers are comparable between calls on
   * the same read object only. A consulted constituent with no events
   * facade is fingerprinted by its cell store's revision
   * (`SourceRow.cellsRevision`, O(1)): it advances the number when one of
   * its cells changes, never on a read. The row's calendar-list config
   * (`peekListConfig`) is part of it too, so a membership a consumer
   * filtered by generation follows a config change.
   */
  generation(): number;
  /**
   * The calendar-list picker config persisted on this read's row
   * (`selection`, `categories`, `visibilityMode`, `customCalendars`,
   * `access`), with its defaults (selection `"all"`, default visibility,
   * access `"editable"`) where the row says nothing. Read at call time from
   * the live row; a row that is not a calendar-list alias answers the
   * defaults.
   */
  peekListConfig(): CalendarListConfig;
  /**
   * Which items the calendar-list row shows (`peekListConfig`): those whose
   * calendar is visible — one of `customCalendars` in custom mode, else as
   * the consumer's user-level `isCalendarVisible` says — and, when the config
   * names categories, whose category is one of them. Apply it to `timed`,
   * `allDay` and `span` items alike; every renderer of the alias does.
   * The predicate is live: it follows the row's config as it changes (a
   * new attribute bag is decoded once, on the next call), so a consumer may
   * hold it across binds.
   */
  membership(
    isCalendarVisible: (acid: AuthScopedCalendarId) => boolean,
  ): (item: EventItemTimed | EventItemAllDay) => boolean;
  /**
   * `membership` with its reason: for each item, whether the row shows it
   * and, when it does not, which rule excluded it (`CalendarEventRead.Verdict`).
   * Live in the same way: it follows the row's config as it changes.
   */
  membershipVerdict(
    isCalendarVisible: (acid: AuthScopedCalendarId) => boolean,
  ): (item: EventItemTimed | EventItemAllDay) => CalendarEventRead.Verdict;
  /**
   * The month's source rows: from this read's row, every row with an events
   * facade that is not a list aggregate, not descending below one (the
   * calendar month's enumeration). It differs from the day read's
   * constituents: below a list aggregate the day read takes the aggregate's
   * live constituents, and a block row's day read stops at its first facade
   * while the month takes every facade row (pinned by tests).
   */
  peekMonthSources(): readonly CalendarEventRead.MonthSource[];
  /**
   * The month's timed members of `range`: each source's facade items that
   * belong to `range` (the facade's order: start, then id), concatenated in
   * source order, first ID wins — a later source's item with an ID already
   * taken is dropped, whatever its origin. A month cell showing `n` events
   * shows the first `n` members: the first source's earliest first.
   */
  peekMonthTimed(range: DateTime.Range<Utc>): readonly CalendarEventRead.MonthMember<EventItemTimed>[];
  /** The month's all-day members of `days`, by the same union as `peekMonthTimed`. */
  peekMonthAllDay(days: NaiveDate.Range): readonly CalendarEventRead.MonthMember<EventItemAllDay>[];
  /**
   * The month source whose facade holds event `id` (the first in source
   * order); `null` when none does — never a fallback row.
   */
  peekMonthOwner(id: EventId): Option<CalendarEventRead.MonthSource>;
  /**
   * Where a new event created from the month goes: the first month source,
   * and whether it may be written (`writable`: the row stores its own cells
   * and the calendar-list config is not read-only). `null` with no source.
   * Existing events keep writing through their carried origin.
   */
  peekCreateTarget(): Option<CalendarEventRead.CreateTarget>;
  /**
   * Where `item` — an item this read answered — comes from: whether it is a
   * stored event (a cell; a materialized occurrence override is one) or an
   * occurrence a facade's recurrence expansion generated (no cell of its
   * own), and the source row whose events hold it (`item.origin`'s data
   * owner), so a consumer neither re-derives storage from the item's id nor
   * resolves the backing row itself. `null` when none of the rows this read
   * consults owns it (an item of another read).
   */
  provenance(item: EventItemTimed | EventItemAllDay): Option<CalendarEventRead.Provenance>;
  /**
   * Readiness for `timed(range, …)`. Resolves once every events facade this
   * read consults has expanded its recurring masters through the day `range`
   * ends on (`EventsFacade.prepare`), so a `timed` read made after it returns
   * every occurrence without polling. Await it, then read synchronously into
   * fresh slots.
   *
   * A constituent whose events change while it waits is prepared for the
   * new events (`EventsFacade.prepare`); a constituent that joins the
   * calendar while it waits is prepared too. Rejects with
   * `EventsFacade.SupersededError` only when events keep changing faster
   * than they expand (bounded progress), or with a failed expansion's
   * error. Constituents without a facade, and the raw lane, need no
   * preparation.
   */
  prepareTimed(range: DateTime.Range<Utc>): Promise<void>;
}

/** Types that go with `CalendarEventRead`: `Span`, `Provenance`, `MonthSource`, `MonthMember`, `CreateTarget`. */
export namespace CalendarEventRead {
  /**
   * Why the calendar-list row shows an item or not (`membershipVerdict`): a
   * member, or the first membership rule that excluded it
   * (`calendar-hidden`, `not-in-custom-list`, `category-excluded`). Declared
   * beside the rule, as `CalendarListConfig.Verdict`.
   */
  export type Verdict = CalendarListConfig.Verdict;

  /** A month source row (`peekMonthSources`). */
  export interface MonthSource {
    /** The live source row. */
    readonly row: SourceRow;
    /** Its joined handle, for a read over a joined tree (`calendarEventRead`); `null` over model rows. */
    readonly joined: Option<SheetRow.Joined.Handle>;
  }

  /** One member of the month union (`peekMonthTimed` / `peekMonthAllDay`). */
  export interface MonthMember<E extends EventItemTimed | EventItemAllDay> {
    /** The facade item, as the facade holds it. */
    readonly item: E;
    /** The member's identity: its id with its origin's data-owning row (an id alone collides across sources). */
    readonly key: string;
    /** The source whose facade answered it. */
    readonly source: MonthSource;
  }

  /** `peekCreateTarget()`: the row a new month event is created in. */
  export interface CreateTarget {
    readonly source: MonthSource;
    /** The row stores its own cells and the calendar-list config is not read-only. */
    readonly writable: boolean;
  }

  /**
   * `CalendarEventRead.provenance(item)`: where a read item comes from, in
   * the shape of a column fact's source owner (`SourceCellOrigin`).
   */
  export interface Provenance {
    /** `"stored"`: a stored event cell; `"expanded"`: an occurrence the expansion generated. */
    readonly occurrence: "stored" | "expanded";
    /** The source row whose events hold the item (an occurrence: its master's row). */
    readonly sourceRow: SourceRow;
    /** The data-owning row writes route to (`item.origin.backingRowId()`). */
    readonly sourceRowId: RowLocalId;
    /** The stored row the source row's cells live in (`sourceRow.backendRowKey()`). */
    readonly backingOwnerKey: RowNetworkKey;
    /** `"derived"` when the source row's cells are derived (`sourceRow.cellsDerived`). */
    readonly storage: "source" | "derived";
  }

  /**
   * `CalendarEventRead.span(range, cache, zone)`: the calendar read over
   * `range`, sliced without reading any lane again. A slice outside `range`
   * (all-day: outside its days in `zone`) is a caller error (it throws).
   */
  export interface Span {
    /** The range every slice must lie in. */
    readonly range: DateTime.Range<Utc>;
    /** `CalendarEventRead.generation()` when this span was read. */
    readonly generation: number;
    /** `CalendarEventRead.timed(sub, cache)`, for `sub` inside `range`. */
    timed(sub: DateTime.Range<Utc>): readonly EventItemTimed[];
    /**
     * `CalendarEventRead.allDay(days, cache)`, its raw lane read through the
     * span's cache, for `days` inside the span's days: the local date
     * `range` starts on to the one it ends on, in the span's zone (UTC's
     * dates when it was read with a `null` zone).
     */
    allDay(days: NaiveDate.Range): readonly EventItemAllDay[];
  }
}

/** Retain the joined handle only; resolve its current model/facades when queried. */
export function calendarEventRead(row: SheetRow.Joined.Handle): CalendarEventRead {
  return new JoinedCalendarEventRead(joinedNode(row));
}

/**
 * The same read over a rows-side model row (`SheetHandle.model`'s rows), for a
 * consumer that has no joined tree: building one (`SheetHandle.joinedTree`)
 * wires formula compute and series subscriptions and replaces the handle's
 * tree. Same union, precedence, membership and readiness as
 * `calendarEventRead`; it walks the model rows' own children.
 */
export function calendarRowRead(row: ModelRowReads): CalendarEventRead {
  return new JoinedCalendarEventRead(modelNode(liveRowOf(row)));
}

class JoinedCalendarEventRead implements CalendarEventRead {
  readonly #lanes: CalendarReadLanes;
  /** What `generation()` last saw, and its number. */
  #fingerprint: readonly unknown[] = [];
  #generation = 0;
  constructor(readonly row: ReadNode) {
    this.#lanes = new CalendarReadLanes(row);
  }

  timed(range: DateTime.Range<Utc>, cache: Option<EventsCacheSlots>): readonly EventItemTimed[] {
    return this.#lanes.timedSlice(this.#lanes.timedLanes(range, cache), range, range);
  }

  allDay(range: NaiveDate.Range, cache: Option<EventsCacheSlots>): readonly EventItemAllDay[] {
    return this.#lanes.allDaySlice(this.#lanes.allDayLanes(range, cache), range, range);
  }

  span(range: DateTime.Range<Utc>, cache: Option<EventsCacheSlots>, zone: Option<TimezoneRegion>): CalendarEventRead.Span {
    const generation = this.generation();
    // The consumer's days, in its zone: east of UTC a local midnight falls on the previous UTC date.
    const days = zone == null
      ? new NaiveDate.Range(range.start.date, range.end.date)
      : new NaiveDate.Range(zone.toTz(range.start).date, zone.toTz(range.end).date);
    let timed: Option<TimedLanes> = null;
    let allDay: Option<AllDayLanes> = null;
    return Object.freeze({
      range,
      generation,
      timed: (sub: DateTime.Range<Utc>) => {
        invariant(range.start.mse <= sub.start.mse && sub.end.mse <= range.end.mse,
          "CalendarEventRead.Span.timed: the slice lies inside the span", {
            span: `${range.start.toString()}..${range.end.toString()}`,
            slice: `${sub.start.toString()}..${sub.end.toString()}`,
          });
        timed ??= this.#lanes.timedLanes(range, cache);
        return this.#lanes.timedSlice(timed, range, sub);
      },
      allDay: (sub: NaiveDate.Range) => {
        invariant(days.start.dse <= sub.start.dse && sub.end.dse <= days.end.dse,
          "CalendarEventRead.Span.allDay: the days lie inside the span", {
            span: `${days.start.toString()}..${days.end.toString()}`,
            zone: String(zone?.fullname ?? "UTC"),
            days: `${sub.start.toString()}..${sub.end.toString()}`,
          });
        allDay ??= this.#lanes.allDayLanes(days, cache);
        return this.#lanes.allDaySlice(allDay, days, sub);
      },
    });
  }

  generation(): number {
    const fingerprint = this.#currentFingerprint();
    const last = this.#fingerprint;
    if (fingerprint.length !== last.length || fingerprint.some((part, i) => part !== last[i])) {
      this.#fingerprint = fingerprint;
      this.#generation++;
    }
    return this.#generation;
  }

  /**
   * Everything a read consults, as identities and change counters: the
   * aggregate and its constituents, the first reachable facade, the raw
   * lane's root and the source rows its walk reads, each with its facade's
   * `peekRevision()`. A facade-less source row contributes its cell store's
   * revision (`CalendarReadLanes.seededRevision`, `SourceRow.cellsRevision`):
   * unchanged cells read the same parts. The row's calendar-list config, as
   * its canonical JSON (membership follows it).
   */
  #currentFingerprint(): readonly unknown[] {
    const parts: unknown[] = [JSON.stringify(this.peekListConfig())];
    const facadeOf = (facade: Option<EventsFacade>, row: SourceRow) => {
      if (facade != null) parts.push(facade, facade.peekRevision());
      else {
        const revision = CalendarReadLanes.seededRevision(row);
        parts.push(null, revision.length, ...revision);
      }
    };
    const aggregate = this.#lanes.aggregateListFacade(this.row);
    parts.push(aggregate);
    if (aggregate) {
      for (const sourceRow of liveConstituentRowsOf(aggregate)) {
        parts.push(sourceRow);
        facadeOf(sourceRow.as(EVENTS_FACADE), sourceRow);
      }
    }
    const first = this.#lanes.firstEventsFacade(this.row);
    parts.push(first, first?.peekRevision() ?? null);
    const variant = this.row.variant();
    parts.push(variant);
    if (variant) {
      for (const sourceRow of this.#lanes.provider.eventSourceRows(variant)) {
        parts.push(sourceRow);
        facadeOf(sourceRow.as(EVENTS_FACADE), sourceRow);
      }
    }
    return parts;
  }

  peekListConfig(): CalendarListConfig {
    return calendarListConfig(this.row.attributesAs(decodeCalendarListAttributes));
  }

  membership(
    isCalendarVisible: (acid: AuthScopedCalendarId) => boolean,
  ): (item: EventItemTimed | EventItemAllDay) => boolean {
    const verdict = this.membershipVerdict(isCalendarVisible);
    return (item) => verdict(item).member;
  }

  membershipVerdict(
    isCalendarVisible: (acid: AuthScopedCalendarId) => boolean,
  ): (item: EventItemTimed | EventItemAllDay) => CalendarEventRead.Verdict {
    // Re-decode only when the row's attribute bag changes (a write replaces the frozen bag).
    let bag: unknown = undefined;
    let verdict: Option<(item: EventItemTimed | EventItemAllDay) => CalendarEventRead.Verdict> = null;
    return (item) => {
      const now = this.row.attributesAs((attributes) => attributes);
      if (verdict == null || now !== bag) {
        bag = now;
        verdict = calendarListVerdict(this.peekListConfig(), isCalendarVisible);
      }
      return verdict(item);
    };
  }

  peekMonthSources(): readonly CalendarEventRead.MonthSource[] {
    return this.#lanes.monthSources().map(JoinedCalendarEventRead.#monthSource);
  }

  peekMonthTimed(range: DateTime.Range<Utc>): readonly CalendarEventRead.MonthMember<EventItemTimed>[] {
    return this.#monthUnion((facade) => facade.peekInRange(range));
  }

  peekMonthAllDay(days: NaiveDate.Range): readonly CalendarEventRead.MonthMember<EventItemAllDay>[] {
    return this.#monthUnion((facade) => facade.peekAllDayInRange(days));
  }

  peekMonthOwner(id: EventId): Option<CalendarEventRead.MonthSource> {
    const owner = this.#lanes.monthSources().find((source) => source.facade.peek(id) != null);
    return owner == null ? null : JoinedCalendarEventRead.#monthSource(owner);
  }

  peekCreateTarget(): Option<CalendarEventRead.CreateTarget> {
    const first = this.#lanes.monthSources()[0];
    if (first == null) return null;
    const source = JoinedCalendarEventRead.#monthSource(first);
    return Object.freeze({ source, writable: !source.row.cellsDerived && this.peekListConfig().access === "editable" });
  }

  /** Concatenate each month source's facade items in source order; first ID wins. */
  #monthUnion<E extends EventItemTimed | EventItemAllDay>(
    read: (facade: EventsFacade) => readonly E[],
  ): readonly CalendarEventRead.MonthMember<E>[] {
    const taken = new Set<string>();
    const out: CalendarEventRead.MonthMember<E>[] = [];
    for (const node of this.#lanes.monthSources()) {
      const source = JoinedCalendarEventRead.#monthSource(node);
      for (const item of read(node.facade)) {
        if (taken.has(item.id)) continue;
        taken.add(item.id);
        out.push(Object.freeze({ item, key: `${item.id}@${item.origin.backingRowId().toString()}`, source }));
      }
    }
    return out;
  }

  static #monthSource(node: MonthSourceNode): CalendarEventRead.MonthSource {
    return Object.freeze({ row: node.row, joined: node.joined });
  }

  provenance(item: EventItemTimed | EventItemAllDay): Option<CalendarEventRead.Provenance> {
    const owner = item.origin.backingRowId();
    const candidates: SourceRow[] = [];
    const aggregate = this.#lanes.aggregateListFacade(this.row);
    if (aggregate) candidates.push(...liveConstituentRowsOf(aggregate));
    const variant = this.row.variant();
    if (variant) candidates.push(...this.#lanes.provider.eventSourceRows(variant));
    const sourceRow = candidates.find((row) => row.dataOwningRowId() === owner);
    if (sourceRow == null) return null;
    return Object.freeze({
      occurrence: sourceRow.as(EVENTS_FACADE)?.peekProvenance(item.id) ?? "stored",
      sourceRow,
      sourceRowId: owner,
      backingOwnerKey: sourceRow.backendRowKey(),
      storage: sourceRow.cellsDerived ? "derived" : "source",
    });
  }

  async prepareTimed(range: DateTime.Range<Utc>): Promise<void> {
    const until = range.end.date.addDays(1);
    // The constituent set can change while the facades prepare (a source
    // moved in or out of the list): prepare until the set `timed` would
    // consult is the one prepared, a bounded number of times.
    let prepared: ReadonlySet<EventsFacade> = new Set();
    for (let round = 0; round < JoinedCalendarEventRead.#MAX_CONSTITUENT_ROUNDS; round++) {
      const facades = this.#consultedFacades();
      const fresh = [...facades].filter((facade) => !prepared.has(facade));
      if (fresh.length === 0) return;
      await Promise.all(fresh.map((facade) => facade.prepare(until)));
      prepared = facades;
    }
    throw new Error(
      `CalendarEventRead.prepareTimed(${until.toString()}): the calendar's constituents kept changing`,
    );
  }

  static readonly #MAX_CONSTITUENT_ROUNDS = 4;

  /** Every facade `timed` may consult: each aggregate constituent's, and the
   *  first reachable one. */
  #consultedFacades(): ReadonlySet<EventsFacade> {
    const facades = new Set<EventsFacade>();
    const aggregate = this.#lanes.aggregateListFacade(this.row);
    if (aggregate) {
      for (const sourceRow of liveConstituentRowsOf(aggregate)) {
        const facade = sourceRow.as(EVENTS_FACADE);
        if (facade) facades.add(facade);
      }
    }
    const reachable = this.#lanes.firstEventsFacade(this.row);
    if (reachable) facades.add(reachable);
    return facades;
  }
}
